import type {
  CustomerZoneGetDefaultResponseType,
  CustomerZoneGetDefaultRequestType,
} from "@sellar-io/api-v2/types";
import { useQuery } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  ApiV2QueryOptions,
  apiV2Request,
  getDefaultQueryOptionsV2,
} from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

type CustomerZonesGetDefaultParams = Omit<
  CustomerZoneGetDefaultRequestType,
  "retailerPostcode"
> & {
  buyerPostcode: string;
};

export const useCustomerZonesGetDefault = <
  TData = CustomerZoneGetDefaultResponseType,
>(
  params: CustomerZonesGetDefaultParams,
  options?: ApiV2QueryOptions<TData>,
) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  const { buyerPostcode, ...apiParams } = params;

  return useQuery({
    queryKey: [
      business?.id,
      ServerStateKeys.CustomerZonesDefault,
      buyerPostcode,
    ],
    queryFn: (queryParams) =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: `customer-zones/default`,
        params: {
          ...apiParams,
          retailerPostcode: buyerPostcode,
          offset: queryParams.pageParam,
        },
      }).then((data) => apiV2AssertResponse<TData>(data).data),
    ...getDefaultQueryOptionsV2({
      context: { scope: "business", user, business: business },
      options: options,
    }),
  });
};
